/* global axios */
import ApiClient from './ApiClient';

// decodeURIComponent throws on malformed sequences (e.g. "%"). Return the
// original string in that case — the backend will still reject it via
// validate_and_fetch_search_query, and we avoid crashing the FE.
const safeDecode = str => {
  if (!str) return '';
  try {
    return decodeURIComponent(str);
  } catch (_e) {
    return str;
  }
};

// Strip leading '+' so phone queries are validated against the same form
// sent to the backend (ContactsView drops the '+' before dispatching).
// Re-trim after stripping so input like "+ a" yields "a" (not " a"), keeping
// the FE guard in agreement with itself when the same value is normalized
// twice (e.g. once raw in the component, once after URL-decode in the API).
// Collapse runs of internal whitespace to a single space so the codepoint
// count + cache-key computation match the backend's
// `q.strip.gsub(/\s+/, ' ')`. Without this, "a  1" reads as 4 codepoints on
// the FE but the BE searches and caches against "a 1" (3 chars) — a subtle
// inconsistency rather than a hard validation gap, but worth closing for
// parity with the backend rules.
export const normalizeSearchQuery = q => {
  if (!q) return '';
  const trimmed = q.trim().replace(/\s+/g, ' ');
  return trimmed.startsWith('+') ? trimmed.slice(1).trim() : trimmed;
};

// Count Unicode codepoints (not UTF-16 code units) so the FE guard mirrors
// Ruby's String#length on the backend. Without this, a single emoji like
// "🔍" reads as length 2 here but length 1 on the server — the FE would
// dispatch and the BE would 422.
const codepointLength = str => Array.from(str).length;

// Mirrors the backend's MAX_SEARCH_QUERY_LENGTH cap in
// app/controllers/api/v1/accounts/contacts_controller.rb. Backend rejects
// queries longer than this with 422; short-circuit on the FE so the user
// doesn't see the generic error toast.
export const MAX_SEARCH_QUERY_LENGTH = 200;

// Mirrors the backend: minimum 2 codepoints for letter-only queries (matches
// the `name` column only), 3 codepoints when the query contains any digit
// (phone/email/identifier fragments need more signal to avoid full-scans).
// Also rejects queries above MAX_SEARCH_QUERY_LENGTH so we mirror the BE
// upper bound and avoid a 422 round-trip.
export const isSearchableQuery = q => {
  const normalized = normalizeSearchQuery(q);
  const len = codepointLength(normalized);
  if (len < 2) return false;
  if (len > MAX_SEARCH_QUERY_LENGTH) return false;
  if (len < 3 && /\d/.test(normalized)) return false;
  return true;
};

// Hint codes for the FE to render an inline message explaining WHY a query is
// not yet searchable. Used by the search box to surface the same min-length
// rules the backend enforces, without surprising the user with a silent
// non-search.
export const SEARCH_HINT_NONE = '';
export const SEARCH_HINT_TOO_SHORT = 'too_short';
export const SEARCH_HINT_DIGIT_NEEDS_MORE = 'digit_needs_more';

export const searchQueryHint = q => {
  const normalized = normalizeSearchQuery(q);
  if (!normalized) return SEARCH_HINT_NONE;
  const len = codepointLength(normalized);
  if (len < 2) return SEARCH_HINT_TOO_SHORT;
  if (len < 3 && /\d/.test(normalized)) return SEARCH_HINT_DIGIT_NEEDS_MORE;
  return SEARCH_HINT_NONE;
};

// Build the shared `q=…&labels[]=…` tail used by /search and /search/count.
// `search` is already URL-encoded by the caller (ContactsView); `label`
// arrives raw and is encoded here so reserved chars (&, =, +) don't
// corrupt the query string.
// Build the shared `q=…&labels[]=…` tail used by /search and /search/count.
// Some callers (ContactsView store action) pass `search` already URL-encoded;
// others (ContactMergeModal) pass it raw. Decoding-then-encoding makes the
// helper idempotent: raw values get encoded, valid percent-sequences round-
// trip cleanly, and malformed input falls back to the original string.
export const buildSearchQuery = (search, label) => {
  const parts = [];
  if (search) parts.push(`q=${encodeURIComponent(safeDecode(search))}`);
  if (label) parts.push(`labels[]=${encodeURIComponent(label)}`);
  return parts.join('&');
};

export const buildContactParams = (page, sortAttr, label, search) => {
  const base = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  const tail = buildSearchQuery(search, label);
  return tail ? `${base}&${tail}` : base;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  getConversations(contactId, { before } = {}) {
    return axios.get(`${this.url}/${contactId}/conversations`, {
      params: before ? { before } : {},
    });
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    // `search` arrives already URL-encoded from ContactsView.vue. Decode
    // before validating so length/digit checks operate on the raw query
    // characters (non-ASCII like "ôé" encodes to "%C3%B4%C3%A9" which
    // would falsely pass an encoded-form length/digit check).
    if (!isSearchableQuery(safeDecode(search))) {
      return Promise.resolve({
        data: { payload: [], meta: { current_page: page } },
      });
    }
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL);
  }

  searchCount(search = '', label = '') {
    if (!isSearchableQuery(safeDecode(search))) {
      return Promise.resolve({ data: { count: 0 } });
    }
    // Share the query-string builder with `search()` so both endpoints
    // encode `label` identically and handle URL-special chars (&, =, +).
    // `search` arrives already URL-encoded; buildSearchQuery passes it
    // through untouched (re-encoding would double-encode).
    const tail = buildSearchQuery(search, label);
    const requestURL = tail
      ? `${this.url}/search/count?${tail}`
      : `${this.url}/search/count`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  importPreview(blobSignedId) {
    return axios.post(`${this.url}/import_preview`, { blob_signed_id: blobSignedId });
  }

  importWithMapping(blobSignedId, columnMapping) {
    return axios.post(`${this.url}/import_with_mapping`, {
      blob_signed_id: blobSignedId,
      column_mapping: columnMapping,
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  destroyContactInbox(contactId, contactInboxId) {
    return axios.delete(`${this.url}/${contactId}/contact_inboxes/${contactInboxId}`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }
}

export default new ContactAPI();
